<template>
  <div class="wager-card">
    <div class="card-meta-row">
      <span class="meta-date">{{ formatDateReadable(transaction.created_at) }}</span>
      <div class="meta-id">
        <span class="meta-id-text">{{ $t('wagers.idShort') }}: {{ transaction.id }}</span>
        <button
          type="button"
          class="copy-button"
          :aria-label="$t('transactionDetails.copyToClipboard')"
          @click.stop="copyToClipboard(String(transaction.id))"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect
              x="5"
              y="5"
              width="9"
              height="9"
              rx="1"
              stroke="currentColor"
              stroke-width="1.25"
            />
            <path
              d="M4 11H3C2.44772 11 2 10.5523 2 10V3C2 2.44772 2.44772 2 3 2H10C10.5523 2 11 2.44772 11 3V4"
              stroke="currentColor"
              stroke-width="1.25"
            />
          </svg>
        </button>
      </div>
    </div>

    <div class="card-title-row">
      <span class="game-name">{{ displayGameName }}</span>
      <span :class="['status-value', formatStatusClass(transaction.bet_status, transaction.total_winnings)]">
        {{ formatStatusLabel(transaction.bet_status, transaction.total_winnings) }}
      </span>
    </div>

    <div class="card-details">
      <div class="detail-row">
        <span class="detail-label">{{ $t('wagers.description') }}:</span>
        <span class="detail-value">{{ $t('wagers.wager') }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">{{ $t('wagers.drawsPlayed') }}:</span>
        <span class="detail-value">{{ drawsPlayedDisplay }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">{{ $t('wagers.totalBetAmount') }}</span>
        <span class="detail-value">{{ formatCurrency(transaction.total, transaction.currency_symbol) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">{{ $t('wagers.totalReturn') }}:</span>
        <span class="detail-value">{{ getTotalReturn(transaction) }}</span>
      </div>
    </div>

    <div class="card-action">
      <button
        type="button"
        class="view-details-btn"
        @click="handleViewDetails"
      >
        {{ $t('wagers.viewDetails') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Transaction } from '@/stores/transactions'
import { formatCurrency } from '@/utils/currency'
import { useTheme } from '@/composables/useTheme'
import { copyTextToClipboard } from '@/utils/copyToClipboard'

const props = defineProps<{
  transaction: Transaction
}>()

const emit = defineEmits<{
  (e: 'view-details', transaction: Transaction): void
  (e: 'rebet'): void
  (e: 'copy-success'): void
  (e: 'copy-failed'): void
}>()

const { t } = useI18n()
const { currentThemeName } = useTheme()
const useWonLostLabels = computed(() => currentThemeName.value === 'wsb')

const displayGameName = computed(() => {
  const name = props.transaction.game_name?.trim()
  return name || '—'
})

const drawsPlayedDisplay = computed(() => {
  const count = props.transaction.draws_played
  if (count === undefined || count === null) {
    return '—'
  }
  return count
})

const formatStatusLabel = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return status
  }
  return parseFloat(String(winnings || 0)) > 0 ? t('wagers.won') : t('wagers.lost')
}

const formatStatusClass = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return String(status).toLowerCase()
  }
  return parseFloat(String(winnings || 0)) > 0 ? 'won' : 'lost'
}

const handleViewDetails = (event: Event) => {
  event.preventDefault()
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.blur()
  }
  emit('view-details', props.transaction)
}

const formatDateReadable = (dateString: string) => {
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, '0')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')

  return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`
}

const getTotalReturn = (transaction: Transaction) => {
  if (transaction.bet_status.toLowerCase() === 'open') {
    return t('wagers.tbc')
  }
  return formatCurrency(transaction.total_winnings, transaction.currency_symbol)
}

const copyToClipboard = async (text: string) => {
  const copied = await copyTextToClipboard(text)
  if (copied) {
    emit('copy-success')
  } else {
    emit('copy-failed')
  }
}
</script>

<script lang="ts">
export default {
  name: 'WagerCard',
}
</script>

<style scoped>
.wager-card {
  background: var(--wagers-list-card-bg);
  margin: 0 0 8px;
  overflow: hidden;
  border: 1px solid var(--wagers-list-card-border);
  border-radius: var(--wagers-card-radius, 8px);
}

.card-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 6px;
}

.meta-date {
  color: var(--wagers-list-meta-text);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.3;
}

.meta-id {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.meta-id-text {
  color: var(--wagers-list-meta-text);
  font-size: 13px;
  font-weight: 400;
}

.copy-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--wagers-list-value-text);
  cursor: pointer;
  opacity: 0.9;
}

.copy-button:hover {
  opacity: 1;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 0 12px 10px;
}

.game-name {
  color: var(--wagers-list-game-name-text);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  min-width: 0;
}

.status-value {
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  text-transform: capitalize;
}

.status-value.open {
  color: var(--wagers-list-status-open);
}

.status-value.settled,
.status-value.won {
  color: var(--wagers-list-status-won);
}

.status-value.lost {
  color: var(--wagers-list-status-lost);
}

.status-value.cancelled {
  color: var(--wagers-list-status-cancelled);
}

.card-details {
  padding: 0;
  border-top: 1px solid var(--wagers-list-card-border);
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--wagers-list-card-border);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--wagers-list-label-text);
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-value {
  color: var(--wagers-list-value-text);
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.card-action {
  background: var(--wagers-list-card-bg);
  text-align: center;
  border-top: 1px solid var(--wagers-list-card-border);
}

.view-details-btn {
  background: none;
  border: none;
  color: var(--wagers-list-action-text);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 12px;
  width: 100%;
}

.wager-card:last-child {
  margin-bottom: 0;
}

@media (min-width: 769px) {
  .wager-card {
    display: none;
  }
}
</style>
